import React, { useCallback, useEffect, useState } from "react";
import { ChevronDown, Droplets, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { userApiService } from "../services/userApiService";

const DURATION_OPTIONS = [
  { value: 60, label: "1 min" },
  { value: 120, label: "2 min" },
  { value: 300, label: "5 min" },
  { value: 600, label: "10 min" },
  { value: 900, label: "15 min" },
];

function Toggle({ checked, onChange, disabled }) {
  return (
    <button
      onClick={onChange}
      type="button"
      disabled={disabled}
      className={`relative h-7 w-12 rounded-full transition-colors disabled:opacity-50 ${checked ? "bg-accent" : "bg-muted"}`}
    >
      <span className={`absolute top-1 h-5 w-5 rounded-full bg-card shadow transition-all ${checked ? "left-6" : "left-1"}`} />
    </button>
  );
}

export default function IrrigationAutoRuleEditor({ deviceId, deviceLabel = "", disabled = false }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    enabled: false,
    soilBelow: 30,
    remindersBeforeAuto: 2,
    notifyIntervalMin: 60,
    durationSec: 300,
    autoStartIfNoResponse: true,
    windowEnabled: false,
    windowStart: "06:00",
    windowEnd: "19:30",
  });

  const loadRule = useCallback(async () => {
    if (!deviceId) return;
    setLoading(true);
    try {
      const data = await userApiService.getIrrigationAutoRule(deviceId);
      const item = data?.item;
      if (item) {
        setForm({
          enabled: item.enabled !== false,
          soilBelow: item.soilBelow ?? 30,
          remindersBeforeAuto: item.remindersBeforeAuto ?? 2,
          notifyIntervalMin: item.notifyIntervalMin ?? 60,
          durationSec: item.durationSec ?? 300,
          autoStartIfNoResponse: item.autoStartIfNoResponse !== false,
          windowEnabled: item.schedule?.enabled === true,
          windowStart: item.schedule?.start || "06:00",
          windowEnd: item.schedule?.end || "19:30",
        });
      }
    } catch {
      toast.error("Failed to load irrigation rule");
    } finally {
      setLoading(false);
    }
  }, [deviceId]);

  useEffect(() => {
    loadRule();
  }, [loadRule]);

  async function handleSave(e) {
    e.preventDefault();
    if (!deviceId || disabled) return;
    setSaving(true);
    try {
      const payload = {
        enabled: form.enabled,
        label: `Low soil — ${deviceLabel || deviceId}`,
        soilBelow: Number(form.soilBelow),
        remindersBeforeAuto: Number(form.remindersBeforeAuto),
        notifyIntervalMin: Number(form.notifyIntervalMin),
        durationSec: Number(form.durationSec),
        autoStartIfNoResponse: form.autoStartIfNoResponse,
        schedule: form.windowEnabled
          ? { enabled: true, start: form.windowStart, end: form.windowEnd, timezone: "Asia/Kolkata" }
          : { enabled: false },
      };
      await userApiService.saveIrrigationAutoRule(deviceId, payload);
      toast.success("Irrigation rule saved");
    } catch (err) {
      toast.error(err?.message || "Failed to save rule");
    } finally {
      setSaving(false);
    }
  }

  async function handleRemove() {
    if (!deviceId || disabled) return;
    setSaving(true);
    try {
      await userApiService.deleteIrrigationAutoRule(deviceId);
      setForm((f) => ({ ...f, enabled: false }));
      toast.success("Irrigation rule removed");
    } catch {
      toast.error("Failed to remove rule");
    } finally {
      setSaving(false);
    }
  }

  const summary = loading
    ? "Loading…"
    : form.enabled
      ? `On · soil < ${form.soilBelow}% · ${Math.round((form.durationSec || 300) / 60)} min`
      : "Off";

  return (
    <div className="rounded-2xl border border-border bg-cream/30 min-w-0 w-full box-border overflow-hidden">
      <div className="flex w-full items-center gap-2 px-4 py-3">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex min-w-0 flex-1 items-center gap-3 text-left hover:opacity-90 transition-opacity"
          aria-expanded={open}
        >
          <ShieldAlert className="h-4 w-4 shrink-0 text-accent" />
          <div className="min-w-0 flex-1">
            <div className="font-medium text-sm">Smart irrigation rule</div>
            <div className="text-xs text-muted-foreground truncate">{summary}</div>
          </div>
          <ChevronDown
            className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`}
          />
        </button>
        {!disabled && !loading && (
          <Toggle
            checked={form.enabled}
            onChange={() => setForm((f) => ({ ...f, enabled: !f.enabled }))}
            disabled={saving}
          />
        )}
      </div>

      {open && (
        <div className="border-t border-border px-4 pb-4 pt-3">
          <p className="text-xs text-muted-foreground mb-3">
            When soil drops below your limit, we notify you in Notifications. Approve to start the pump, deny to block
            auto-start until soil recovers. If you ignore reminders, pump can auto-start.
          </p>

          {loading ? (
            <p className="text-sm text-muted-foreground">Loading irrigation rule…</p>
          ) : (
            <form onSubmit={handleSave} className="grid gap-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="grid gap-1 text-sm">
                  <span className="text-muted-foreground">Start when soil below (%)</span>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    className="rounded-xl border border-input bg-cream/50 px-3 py-2"
                    value={form.soilBelow}
                    onChange={(e) => setForm((f) => ({ ...f, soilBelow: e.target.value }))}
                    disabled={disabled}
                  />
                </label>
                <label className="grid gap-1 text-sm">
                  <span className="text-muted-foreground">Pump duration</span>
                  <select
                    className="rounded-xl border border-input bg-cream/50 px-3 py-2"
                    value={form.durationSec}
                    onChange={(e) => setForm((f) => ({ ...f, durationSec: Number(e.target.value) }))}
                    disabled={disabled}
                  >
                    {DURATION_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <label className="grid gap-1 text-sm">
                  <span className="text-muted-foreground">Reminders before auto-start</span>
                  <input
                    type="number"
                    min={1}
                    max={10}
                    className="rounded-xl border border-input bg-cream/50 px-3 py-2"
                    value={form.remindersBeforeAuto}
                    onChange={(e) => setForm((f) => ({ ...f, remindersBeforeAuto: e.target.value }))}
                    disabled={disabled}
                  />
                </label>
                <label className="grid gap-1 text-sm">
                  <span className="text-muted-foreground">Minutes between reminders</span>
                  <input
                    type="number"
                    min={1}
                    max={1440}
                    className="rounded-xl border border-input bg-cream/50 px-3 py-2"
                    value={form.notifyIntervalMin}
                    onChange={(e) => setForm((f) => ({ ...f, notifyIntervalMin: e.target.value }))}
                    disabled={disabled}
                  />
                </label>
              </div>

              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.autoStartIfNoResponse}
                  onChange={(e) => setForm((f) => ({ ...f, autoStartIfNoResponse: e.target.checked }))}
                  disabled={disabled}
                />
                <span>Auto-start pump if I do not respond after all reminders</span>
              </label>

              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.windowEnabled}
                  onChange={(e) => setForm((f) => ({ ...f, windowEnabled: e.target.checked }))}
                  disabled={disabled}
                />
                <span>Only between times (optional safety window)</span>
              </label>

              {form.windowEnabled && (
                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="grid gap-1 text-sm">
                    <span className="text-muted-foreground">From</span>
                    <input
                      type="time"
                      className="rounded-xl border border-input bg-cream/50 px-3 py-2"
                      value={form.windowStart}
                      onChange={(e) => setForm((f) => ({ ...f, windowStart: e.target.value }))}
                      disabled={disabled}
                    />
                  </label>
                  <label className="grid gap-1 text-sm">
                    <span className="text-muted-foreground">Until</span>
                    <input
                      type="time"
                      className="rounded-xl border border-input bg-cream/50 px-3 py-2"
                      value={form.windowEnd}
                      onChange={(e) => setForm((f) => ({ ...f, windowEnd: e.target.value }))}
                      disabled={disabled}
                    />
                  </label>
                </div>
              )}

              {!disabled && (
                <div className="flex flex-wrap gap-2 justify-end">
                  <Button type="button" variant="outline" onClick={handleRemove} disabled={saving}>
                    Remove rule
                  </Button>
                  <Button type="submit" disabled={saving}>
                    <Droplets className="h-4 w-4" />
                    Save rule
                  </Button>
                </div>
              )}
            </form>
          )}
        </div>
      )}
    </div>
  );
}
